import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import { AllCommunityModule, type StateUpdatedEvent } from "ag-grid-community";
import { AgGridProvider } from "ag-grid-react";
const modules = [AllCommunityModule];

import { Button, FormControl, Input, MenuItem, Select, Tooltip } from "@mui/material";
import { DatePicker, DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import { useStableHandlers } from "@/modules/stable_handlers";

import { CustomAgGridReact, type CustomColDef, type CustomColGroupDef } from "@/components/aggrid/custom_ag_grid_react";

import router from "@user/router";
import { useTabsStore } from "@user/store/tabs_store";
import { useAuthStore } from "@user/store/auth_store";
import { useSettingsStore } from "@user/store/settings_store";

interface State {
}

const defState = {
};

interface SearchParams {
  errorDateFrom: string,
  errorDateTo: string,
  workEquip: string,
  errorCodeLike: string,
  export: string,
  page: number,
  pageSize: number
}

const defSearchParams = {
  errorDateFrom: dayjs().format("YYYYMMDD") + "0000",
  errorDateTo: dayjs().format("YYYYMMDD") + "2359",
  workEquip: "",
  errorCodeLike: "",
  export: "",
  page: 1,
  pageSize: kutil.DEFAULT_PAGE_SIZE
};

interface Result {
  searchParams: SearchParams,
  pageInfo: PageInfo,
  infos: any[]
}

const defResult = {
  searchParams: kutil.deepCopy(defSearchParams),
  pageInfo: {
    totalCount: 0,
    totalPage: 0,
    page: defSearchParams.page,
    pageSize: defSearchParams.pageSize
  },
  infos: []
};

function EquipmentErrorLog(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [searchParams, setSearchParams] = useState<SearchParams>(kutil.deepCopy(defSearchParams));
  const [result, setResult] = useState<Result>(kutil.deepCopy(defResult));

  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const location = useTabsStore((state) => state.getLocation(props.id));
  const gridState = useMemo(() => useSettingsStore.getState().getGridState(location.path), [location.path]);

  const searchParamsRef = useRef(searchParams);
  const updateSearchParams = useCallback((patch: Partial<SearchParams>, isReset: boolean = false) => {
    if (isReset) {
      searchParamsRef.current = { ...patch } as SearchParams;
    } else {
      searchParamsRef.current = { ...searchParamsRef.current, ...patch };
    }
    setSearchParams(searchParamsRef.current);
    return searchParamsRef.current;
  }, []);

  const gridInfo = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    { headerName: "오류일시", field: "errorDate", width: 200, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.date("yyyy-MM-dd HH:mm:ss") },
    { headerName: "설비", field: "workEquip", width: 150, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("workEquip") },
    { headerName: "오류코드", headerClass: "hd-ta-left", field: "errorCode", width: 300, cellStyle: {textAlign: "left"} },
    { headerName: "오류내용", headerClass: "hd-ta-left", field: "errorContent", width: 300, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} }
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridCallbacks = useStableHandlers({
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, location.path);
    }
  });

  // location 변경시 처리
  useEffect(() => {
    init();
  }, [location.key]);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await searchInfos();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    const searchParams = kutil.deepCopy(defSearchParams);
    kutil.copyObj(Object.fromEntries(new URLSearchParams(location.search).entries()), searchParams);
    updateSearchParams(searchParams);
  }

  async function searchInfos() {
    const searchParams = searchParamsRef.current;

    try {
      const reqSearchParams = kutil.copyObj(searchParams, {} as SearchParams);
      const data = await kutil.sendGet("/api/log/equipment_error_log.data?mode=search", reqSearchParams, {});
      setResult({
        searchParams: reqSearchParams,
        pageInfo: data.pageInfo,
        infos: data.infos,
      });
    } catch(e) {
      console.log(e);
    }
  }
  
  async function doSearch(_e: any, reqParams: any|null = null) {
    const searchParams = updateSearchParams({ ...reqParams, page: 1 });
    router.navigateByPath("/log/equipment_error_log", searchParams);
  }

  const goPage = useCallback((page: number) => {
    const searchParams = updateSearchParams({ ...result.searchParams, page }, true);
    router.navigateByPath("/log/equipment_error_log", searchParams);
  }, [result, router, updateSearchParams]);

  async function resetParams() {
    router.navigateByPath("/log/equipment_error_log", {});
  }

  async function downloadExcel() {
    const searchParams = kutil.copyObj(result.searchParams, {} as SearchParams);
    searchParams.export = "excel";
    searchParams.page = 1;
    searchParams.pageSize = 10000;
    try {
      await kutil.downloadFile("/api/log/equipment_error_log.download?mode=search", searchParams, {
        "fileName": "설비오류로그_" + kutil.date2string(kutil.getCurrLocalDate(), "yyyyMMddHHmmss") + ".xlsx"
      });
    } catch(e) {
      console.log(e);
    }
  }

  return (
    <>
      {/* contents */}
      <div className="contents list">
        {/* contents 상단 */}
        <div className="contents-top">
          {/* 타이틀 영역 */}
          <div className="title-area">
            {/* 타이틀 */}
            <p>설비 오류 로그</p>
          </div>
          {/*// 타이틀 영역 */}
          {/* 버튼 영역 */}
          <div className="top-btn-area">
            <Button variant="outlined" onClick={doSearch}>조회</Button>
            { auth.download &&
            <Button variant="outlined" onClick={downloadExcel}>엑셀다운</Button>
            }
          </div>
          {/*// 버튼 영역 */}
        </div>
        {/*// contents 상단 */}
        {/* 검색 */}
        <div className="srch-area">
          <div className="date-time-range">
            <p>조회일시</p>
            <div>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  ampm={false}
                  timeSteps={{ hours: 1, minutes: 1 }}
                  sx={{ width: 200 }}
                  slotProps={{ textField: { size: "small" }, field: { readOnly: true } }}
                  format="YYYY-MM-DD HH:mm"
                  value={dayjs(searchParams.errorDateFrom)}
                  onChange={(e) => doSearch(e, { errorDateFrom: e != null ? dayjs(e).format("YYYYMMDDHHmm") : "" })}
                  maxDateTime={dayjs(searchParams.errorDateTo)}
                />
                <span>-</span>
                <DateTimePicker
                  ampm={false}
                  timeSteps={{ hours: 1, minutes: 1 }}
                  sx={{ width: 200 }}
                  slotProps={{ textField: { size: "small" }, field: { readOnly: true } }}
                  format="YYYY-MM-DD HH:mm"
                  value={dayjs(searchParams.errorDateTo)}
                  onChange={(e) => doSearch(e, { errorDateTo: e != null ? dayjs(e).format("YYYYMMDDHHmm") : "" })}
                  minDateTime={dayjs(searchParams.errorDateFrom)}
                />
              </LocalizationProvider>
            </div>
          </div>
          <div>
            <p>설비</p>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <Select
                value={searchParams.workEquip}
                onChange={(e) => {doSearch(e, {workEquip: e.target.value});}}
                displayEmpty
              >
                <MenuItem value="">전체</MenuItem>
                {(commonCode.workEquip as any[]).map((code) => (
                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div>
            <p>오류코드</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.errorCodeLike}
              onChange={(e) => {updateSearchParams({ errorCodeLike: e.target.value })}}
              onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
              placeholder="오류코드"
            />
          </div>
          <p>
            <Tooltip title="초기화">
              <button className="icon-button form-reset" onClick={resetParams}>초기화</button>
            </Tooltip>
          </p>
        </div>
        {/*// 검색 */}
        {/* 로그 정보 */}
        <AgGridProvider modules={modules}>
          <div className="grid-area">
            <CustomAgGridReact
              columnDefs={gridInfo}
              rowData={result.infos}
              defaultColDef={kutil.defaultColDef}
              initialState={gridState}
              onStateUpdated={gridCallbacks.onStateUpdated}
              totalPage={result.pageInfo.totalPage}
              currPage={result.pageInfo.page}
              onPageChange={goPage}
            />
          </div>
        </AgGridProvider>
        {/*// 로그 정보 */}
      </div>
      {/*// contents */}
    </>
  );
}

export default React.memo(EquipmentErrorLog);
